import { aceButtonCssVariables } from "./ace-button.get-names";
import { h, Host } from "@stencil/core";
import { processOptionalProperty, JsonParser, mergeClassNames } from "../../utils";
import { getAceButtonNames } from "./ace-button.get-names";
export class AceButton {
    constructor() {
        this.type = 'primary';
        this.disabled = false;
        this.name = null;
        this.fullHeight = false;
        this.fullWidth = false;
        this.fullWidthMobile = false;
        this.fullWidthTablet = false;
        this.iconName = undefined;
        this.size = 'medium';
        this.href = '';
        this.target = null;
        this.loading = false;
        this.disableAnimations = false;
        this.highlight = '';
        this.circular = undefined;
        this.loaderDelay = 0;
        this._disabled = undefined;
        this._loading = undefined;
    }
    componentWillLoad() {
        var _a;
        this.names = getAceButtonNames((_a = this.hostElement) === null || _a === void 0 ? void 0 : _a.id);
        if (this.circular === undefined) {
            this._hasSlotContent = this.hostElement.textContent.trim() !== '';
        }
        this.onIconNameChanged();
        this.onDisabledOrLoadingChanged();
        this.onHighlightChanged();
    }
    onIconNameChanged() {
        this._iconName = processOptionalProperty(this.iconName);
    }
    onDisabledOrLoadingChanged() {
        const newLoadingValue = !!this.loading;
        if (this.loaderDelay && this.loaderDelay > 0) {
            if (this.loaderDelayTimeout) {
                // Clear the timeout as the loading state changed again before the delay was over.
                clearTimeout(this.loaderDelayTimeout);
                this.loaderDelayTimeout = null;
                this._loading = newLoadingValue;
            }
            else {
                // The timeout is set only when we are activating the loader.
                if (newLoadingValue) {
                    this.loaderDelayTimeout = setTimeout(() => {
                        this._loading = newLoadingValue;
                    }, this.loaderDelay);
                }
            }
        }
        else {
            this._loading = newLoadingValue;
        }
        this._disabled = !!(this.disabled || newLoadingValue);
    }
    onHighlightChanged() {
        const highlight = new JsonParser(this.highlight).getValue();
        this._isHighlighted = !!highlight;
        if (typeof highlight === 'object' && this._isHighlighted) {
            if (highlight.background) {
                this.hostElement.style.setProperty(aceButtonCssVariables.HIGHLIGHT_BACKGROUND, highlight.background);
            }
            if (highlight.delay) {
                this.hostElement.style.setProperty(aceButtonCssVariables.HIGHLIGHT_ANIMATION_DELAY, highlight.delay + 'ms');
            }
            if (highlight.duration) {
                this.hostElement.style.setProperty(aceButtonCssVariables.HIGHLIGHT_ANIMATION_DURATION, highlight.duration + 'ms');
            }
            if (highlight.iterations) {
                this.hostElement.style.setProperty(aceButtonCssVariables.HIGHLIGHT_ANIMATION_ITERATION_COUNT, highlight.iterations + '');
            }
        }
    }
    getFinalCssClasses() {
        const { CLASS } = this.names;
        return mergeClassNames(getTypeClassName(this.type, this.names), {
            [CLASS.ROOT]: true,
            [CLASS.CIRCULAR]: this.circular || (this.iconName && this._hasSlotContent === false),
            [CLASS.DISABLED]: this._disabled,
            [CLASS.FULL_HEIGHT]: this.fullHeight,
            [CLASS.FULL_WIDTH]: this.fullWidth,
            [CLASS.FULL_WIDTH_MOBILE]: this.fullWidthMobile,
            [CLASS.FULL_WIDTH_TABLET]: this.fullWidthTablet,
            [CLASS.SIZE_EXTRA_SMALL]: this.size === 'xs' || this.size === 'extra-small',
            [CLASS.SIZE_SMALL]: this.size === 's' || this.size === 'small',
            [CLASS.SIZE_MEDIUM]: this.size === 'm' || this.size === 'medium',
            [CLASS.SIZE_LARGE]: this.size === 'l' || this.size === 'large',
            [CLASS.TYPE_DISABLED(this.type)]: this._disabled,
            [CLASS.HIGHLIGHT]: this._isHighlighted,
            [CLASS.DISABLE_ANIMATIONS]: this.disableAnimations,
        });
    }
    getIconColor() {
        if (this.disabled) {
            return 'text-neutral--disabled';
        }
        else {
            switch (this.type) {
                case 'secondary':
                case 'tertiary':
                case 'hidden':
                    return 'text-neutral--interactive';
                case 'hidden-inverted': // Used for the tooltip mobile modal.
                    return 'var(--ace-semantic-buttons-general-button-hidden-inverted-color)';
                case 'hidden-dark': // Used for the shared jackpot swiper control buttons.
                    return 'text-neutral--interactive-uni-light';
                case 'primary':
                default:
                    return 'text-neutral--interactive-uni-light';
            }
        }
    }
    handleClick(event) {
        if (this._disabled) {
            event.preventDefault();
            return;
        }
        if (this.loading) {
            event.preventDefault();
            return;
        }
        if (this.href) {
            event.stopPropagation();
            this.linkEl.click();
        }
        this.buttonClicked.emit(event);
    }
    renderButtonContent() {
        let cssClassList = {
            [this.names.CLASS.CONTENT__WRAPPER]: true,
            [this.names.CLASS.CONTENT__WRAPPER____LOADING]: this._loading,
        };
        const forceDarkLoaderOnThese = ['secondary-dark', 'tertiary-dar', 'hidden-dark'];
        const loaderForceMode = forceDarkLoaderOnThese.includes(this.type) ? 'dark' : null;
        const isExtraSmall = this.size === 'xs' || this.size === 'extra-small';
        const iconSize = isExtraSmall ? '1.25rem' : '1.5rem';
        const loaderSize = isExtraSmall ? 'xx-small' : 'x-small';
        // Not sure how to handle the CSS classes of sub elements that do not need to be exposed through the getNames function.
        return (h("span", { class: this.names.CLASS.CONTENT }, this.loading && h("ace-loader", { show: true, size: loaderSize, "force-mode": loaderForceMode }), h("span", { class: cssClassList }, this._iconName ? h("ace-icon", { part: 'icon', name: this._iconName, size: iconSize, color: this.getIconColor() }) : null, h("slot", null))));
    }
    /**
     * @private
     */
    renderButtonElement(commonAttributes) {
        return (h("button", Object.assign({}, commonAttributes, { name: this.name }), this.renderButtonContent()));
    }
    renderAnchorElement(commonAttributes) {
        return (h("a", Object.assign({}, commonAttributes, { href: this.href, target: this.target, class: this.getFinalCssClasses(), ref: (linkEl) => this.linkEl = linkEl, onClick: (e) => e.stopPropagation() }), this.renderButtonContent()));
    }
    render() {
        const commonAttributes = {
            'id': this.names.ID.ROOT,
            'class': this.getFinalCssClasses(),
            'part': this.names.PART.ROOT,
            'aria-disabled': this._disabled.toString(),
        };
        // Careful! Since we're triggering a native event, the onClick must be added to the host element
        // (e.g. this is like adding it to the <ace-button> tag, such as <ace-button onClick=...>).
        // This is needed in this case as we want to trigger a custom event based on a native event.
        // This is applicable to any event attached at the host element level.
        return h(Host, { key: '4f2b3c2592a205e297b06d896cdf5aa214ff1248', onClick: e => this.handleClick(e) }, this.href ? this.renderAnchorElement(commonAttributes) : this.renderButtonElement(commonAttributes));
    }
    static get is() { return "ace-button"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["./ace-button.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ace-button.css"]
        };
    }
    static get properties() {
        return {
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceButtonType",
                    "resolved": "\"hidden\" | \"hidden-dark\" | \"hidden-inverted\" | \"primary\" | \"secondary\" | \"secondary-dark\" | \"tertiary\" | \"tertiary-dark\"",
                    "references": {
                        "IAceButtonType": {
                            "location": "import",
                            "path": "./ace-button.types",
                            "id": "src/components/ace-button/ace-button.types.ts::IAceButtonType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "type",
                "reflect": false,
                "defaultValue": "'primary'"
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "disabled",
                "reflect": true,
                "defaultValue": "false"
            },
            "name": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "name",
                "reflect": false,
                "defaultValue": "null"
            },
            "fullHeight": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "full-height",
                "reflect": true,
                "defaultValue": "false"
            },
            "fullWidth": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "full-width",
                "reflect": true,
                "defaultValue": "false"
            },
            "fullWidthMobile": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "full-width-mobile",
                "reflect": true,
                "defaultValue": "false"
            },
            "fullWidthTablet": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "full-width-tablet",
                "reflect": true,
                "defaultValue": "false"
            },
            "iconName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceIconName",
                    "resolved": "string",
                    "references": {
                        "IAceIconName": {
                            "location": "import",
                            "path": "../ace-icon/ace-icon.types",
                            "id": "src/components/ace-icon/ace-icon.types.ts::IAceIconName"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "icon-name",
                "reflect": false
            },
            "size": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceButtonSize",
                    "resolved": "\"extra-small\" | \"l\" | \"large\" | \"m\" | \"medium\" | \"s\" | \"small\" | \"xs\"",
                    "references": {
                        "IAceButtonSize": {
                            "location": "import",
                            "path": "./ace-button.types",
                            "id": "src/components/ace-button/ace-button.types.ts::IAceButtonSize"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "size",
                "reflect": false,
                "defaultValue": "'medium'"
            },
            "href": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "href",
                "reflect": false,
                "defaultValue": "''"
            },
            "target": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ILinkTarget",
                    "resolved": "\"_blank\" | \"_parent\" | \"_self\" | \"_top\"",
                    "references": {
                        "ILinkTarget": {
                            "location": "import",
                            "path": "../../types",
                            "id": "src/types.ts::ILinkTarget"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "target",
                "reflect": false,
                "defaultValue": "null"
            },
            "loading": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "loading",
                "reflect": false,
                "defaultValue": "false"
            },
            "disableAnimations": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "disable-animations",
                "reflect": false,
                "defaultValue": "false"
            },
            "highlight": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "highlight",
                "reflect": false,
                "defaultValue": "''"
            },
            "circular": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "circular",
                "reflect": false
            },
            "loaderDelay": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "loader-delay",
                "reflect": false,
                "defaultValue": "0"
            }
        };
    }
    static get states() {
        return {
            "_disabled": {},
            "_loading": {}
        };
    }
    static get events() {
        return [{
                "method": "buttonClicked",
                "name": "buttonClicked",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "MouseEvent",
                    "resolved": "MouseEvent",
                    "references": {
                        "MouseEvent": {
                            "location": "global",
                            "id": "global::MouseEvent"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "hostElement"; }
    static get watchers() {
        return [{
                "propName": "iconName",
                "methodName": "onIconNameChanged"
            }, {
                "propName": "disabled",
                "methodName": "onDisabledOrLoadingChanged"
            }, {
                "propName": "loading",
                "methodName": "onDisabledOrLoadingChanged"
            }, {
                "propName": "highlight",
                "methodName": "onHighlightChanged"
            }];
    }
}
function getTypeClassName(type, names) {
    switch (type) {
        case 'primary':
            return names.CLASS.TYPE_PRIMARY;
        case 'secondary':
            return names.CLASS.TYPE_SECONDARY;
        case 'secondary-dark':
            return names.CLASS.TYPE_SECONDARY_DARK;
        case 'tertiary':
            return names.CLASS.TYPE_TERTIARY;
        case 'tertiary-dark':
            return names.CLASS.TYPE_TERTIARY_DARK;
        case 'hidden':
            return names.CLASS.TYPE_HIDDEN;
        case 'hidden-dark':
            return names.CLASS.TYPE_HIDDEN_DARK;
        case 'hidden-inverted':
            return names.CLASS.TYPE_HIDDEN_INVERTED;
    }
}
//# sourceMappingURL=ace-button.js.map
